// ─── Compliance-alert helpers ─────────────────────────────────────────────────
//
// Pure, dependency-free logic for compliance alerts surfaced on the dashboard.
// Compliance alerts are proxied from the TMAgent product API via the bridge
// (`/api/bridge/compliance-alerts`). That payload is shaped by an EXTERNAL
// service, so although our `ComplianceAlert` type declares `risk_level` (and the
// string fields) as always-present, a real record can arrive with `risk_level`
// missing/null. The dashboard's render path used to call
// `alert.risk_level.toUpperCase()` directly, so a single field-less record threw
// `Cannot read properties of undefined (reading 'toUpperCase')` and — with no
// error boundary — white-screened the whole command centre.
//
// These helpers are null-safe by construction, and `normalizeComplianceAlert`
// coerces each record to a valid shape at the data boundary so every downstream
// consumer (NeedsAttention, QuickActions, the notification count) is guaranteed
// clean data.

import type { ComplianceAlert } from './api'

export const RISK_LEVELS = ['critical', 'high', 'medium', 'low'] as const
export type RiskLevel = (typeof RISK_LEVELS)[number]

const RISK_RANK: Record<RiskLevel, number> = { critical: 4, high: 3, medium: 2, low: 1 }

/**
 * Coerce any raw value into a known risk level. Anything missing, null, or
 * unrecognised falls back to `medium` — a neutral, non-alarming default that
 * keeps the record visible (and sortable) rather than dropping or mis-ranking it.
 */
export function normalizeRisk(value: unknown): RiskLevel {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : ''
  return (RISK_LEVELS as readonly string[]).includes(v) ? (v as RiskLevel) : 'medium'
}

/** Severity ordering used for sorting/filtering. Unknown → medium rank (2). */
export function riskRank(risk: string | null | undefined): number {
  return RISK_RANK[normalizeRisk(risk)]
}

/** Badge variant for a risk level. Null-safe; unknown → the neutral `normal`. */
export function riskVariant(risk: string | null | undefined): 'critical' | 'high' | 'low' | 'normal' {
  switch (normalizeRisk(risk)) {
    case 'critical': return 'critical'
    case 'high':     return 'high'
    case 'low':      return 'low'
    default:         return 'normal'
  }
}

/** Uppercase badge label for a risk level. Never throws on missing data. */
export function riskLabel(risk: string | null | undefined): string {
  return normalizeRisk(risk).toUpperCase()
}

/**
 * Defensive normalization for a single compliance alert coming off the product
 * bridge. Coerce every field the UI assumes is always present so one malformed
 * record can never crash the render. Applied at the hook boundary
 * (`useComplianceAlerts`) so it runs exactly once per fetch.
 */
export function normalizeComplianceAlert(raw: ComplianceAlert): ComplianceAlert {
  return {
    ...raw,
    risk_level: normalizeRisk(raw.risk_level),
    item_type: typeof raw.item_type === 'string' ? raw.item_type : '',
    tenant_name: typeof raw.tenant_name === 'string' ? raw.tenant_name : 'Unknown tenant',
    description: typeof raw.description === 'string' ? raw.description : '',
  }
}
